import { useFragment } from "@/generated";
import {
  AddAudioTracksDocument,
  type AudioTrackFieldsFragment,
  AudioTrackFieldsFragmentDoc,
  DeleteAudioTracksDocument,
  RenameAudioTrackDocument,
  ReorderAudioTrackDocument,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";

/**
 * Files per upload request. Must not exceed the API's graphql-upload `maxFiles` cap, and
 * keeps a request short enough to finish inside the load balancer's 120s idle timeout —
 * the resolver buffers every file in the request before writing any of them to S3.
 */
const UPLOAD_CHUNK_SIZE = 10;

const UPLOAD_TOAST_ID = "addAudioTracks";

/**
 * Uploads audio files into a folder, a chunk of files per request so the user can pick as
 * many as they like in one go. Chunks run in sequence: the server appends each batch after
 * the folder's current last track, so sequential requests preserve the picked order.
 *
 * A chunk may fail part-way through, keeping the files uploaded before the failure. Rather
 * than reconcile a partial success into the cache by hand, the folder's track list is
 * refetched — it is one small query, and it is the only way the list is guaranteed to match
 * the server after a partial failure.
 */
export function useAddAudioTracks() {
  const [addAudioTracksMutation] = useMutation(AddAudioTracksDocument, {
    refetchQueries: ["GetAudioTracksByFolderId"],
    awaitRefetchQueries: true,
  });
  // Not the mutation's own flag: one call spans several requests, and that flag dips
  // between them, flickering the upload area back to its idle state mid-batch.
  const [loading, setLoading] = useState(false);

  const addAudioTracks = async (folderId: string, files: File[]) => {
    const chunks: File[][] = [];
    for (let i = 0; i < files.length; i += UPLOAD_CHUNK_SIZE) {
      chunks.push(files.slice(i, i + UPLOAD_CHUNK_SIZE));
    }

    let uploaded = 0;
    setLoading(true);

    try {
      return await withToast(
        async () => {
          const created: AudioTrackFieldsFragment[] = [];

          for (const chunk of chunks) {
            if (chunks.length > 1) {
              toast.loading(
                `Uploading ${uploaded + 1}-${uploaded + chunk.length} of ${files.length}...`,
                { id: UPLOAD_TOAST_ID }
              );
            }

            const result = await addAudioTracksMutation({
              variables: { folderId, files: chunk },
            });
            created.push(
              ...(result.data?.addAudioTracks ?? []).map((track) =>
                useFragment(AudioTrackFieldsFragmentDoc, track)
              )
            );
            uploaded += chunk.length;
          }

          return created;
        },
        {
          loading: files.length === 1 ? "Uploading audio..." : `Uploading ${files.length} files...`,
          success:
            files.length === 1 ? "Audio uploaded" : `Successfully uploaded ${files.length} files`,
          error: (error) => {
            const reason =
              error instanceof Error ? error.message : "Failed to upload audio. Please try again.";
            return uploaded > 0
              ? `${reason} ${uploaded} of ${files.length} file(s) were added before it.`
              : reason;
          },
        },
        UPLOAD_TOAST_ID
      );
    } finally {
      setLoading(false);
    }
  };

  return { addAudioTracks, loading };
}

export function useRenameAudioTrack() {
  const [renameAudioTrackMutation, { loading }] = useMutation(RenameAudioTrackDocument);

  const renameAudioTrack = async (id: string, name: string) => {
    return withToast(
      async () => {
        const result = await renameAudioTrackMutation({ variables: { id, name } });
        return result.data?.renameAudioTrack;
      },
      {
        loading: "Renaming...",
        success: "Track renamed",
        error: "Failed to rename track. Please try again.",
      },
      "renameAudioTrack"
    );
  };

  return { renameAudioTrack, loading };
}

export function useDeleteAudioTracks() {
  const [deleteAudioTracksMutation, { loading }] = useMutation(DeleteAudioTracksDocument, {
    update(cache, { data }) {
      for (const id of data?.deleteAudioTracks ?? []) {
        const cacheId = cache.identify({ __typename: "AudioTrack", id });
        if (cacheId) cache.evict({ id: cacheId });
      }
      cache.gc();
    },
  });

  const deleteAudioTracks = async (ids: string[]) => {
    return withToast(
      async () => {
        const result = await deleteAudioTracksMutation({ variables: { ids } });
        return result.data?.deleteAudioTracks ?? [];
      },
      {
        loading: "Deleting...",
        success: ids.length === 1 ? "Track deleted" : `${ids.length} tracks deleted`,
        error: "Failed to delete. Please try again.",
      },
      "deleteAudioTracks"
    );
  };

  return { deleteAudioTracks, loading };
}

/**
 * Reorders a track. The mutation returns every track whose sortOrder changed — normally
 * just the moved one, but the whole folder when a collision forced a renumber — and
 * writing them all back keeps the cached list from mixing fresh and stale orders.
 */
export function useReorderAudioTrack() {
  const [reorderAudioTrackMutation, { loading }] = useMutation(ReorderAudioTrackDocument);

  const reorderAudioTrack = async (
    id: string,
    prevSortOrder: number | null,
    newSortOrder: number | null
  ) => {
    const result = await reorderAudioTrackMutation({
      variables: { id, prevSortOrder, newSortOrder },
    });
    return (result.data?.reorderAudioTrack ?? []).map((track) =>
      useFragment(AudioTrackFieldsFragmentDoc, track)
    );
  };

  return { reorderAudioTrack, loading };
}
